import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { IncorrectPaymentCommand } from '../../command/incorrect-payment';
import { IncorrectPayment } from '../../model/incorrect-payment';
import { ChangePayerCommandHandler } from '../../service/command/change-payer-command-handler.servcice';
import { ContactBalanceCommandHandler } from '../../service/command/contact-balance-command-handler.service';
import { IncorrectPaymentCommandHandlerResolver } from '../../service/command/incorrect-payment-command-handler-resolver.service';
import { PayInvoiceCommandHandler } from '../../service/command/pay-invoice-command-handler.service';
import { VoidPaymentCommandHandler } from '../../service/command/void-payment-command-handler.service';
import { IncorrectPaymentService } from '../../service/data/incorrect-payment.service';
import { CompanyService } from '../../../rent/service/data/company.service';
import { DimensionLabelsService } from '../../../accounting/service/data/dimension-labels.service';
import { DimensionLabels, defaultDimensionLabels } from '../../../accounting/model/dimension-labels';
import { map, switchMap } from 'rxjs/operators';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { UserAccountService } from '../../../common/service/user-account.service';

@Component({
    selector: 'app-incorrect-payment-form',
    templateUrl: './incorrect-payment-form.component.html',
    styleUrls: ['./incorrect-payment-form.component.scss'],
    providers: [
        IncorrectPaymentCommandHandlerResolver,
        ChangePayerCommandHandler,
        PayInvoiceCommandHandler,
        ContactBalanceCommandHandler,
        VoidPaymentCommandHandler,
        { provide: DataService, useExisting: IncorrectPaymentService },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class IncorrectPaymentFormComponent extends FormComponentBase<IncorrectPayment> {
  canManageIncorrectPayments = false;

  constructor(
    private _fb: FormBuilder,
    private _fc: FormControlExFactory,
    activatedRoute: ActivatedRoute,
    commandDispatcher: CommandDispatcher,
    factory: ColumnDefinitionFactory,
    handlerResolver: IncorrectPaymentCommandHandlerResolver,
    service: IncorrectPaymentService,
    private readonly _companyService: CompanyService,
    private readonly _dimensionLabelsService: DimensionLabelsService,
    messageService: MessageService,
    navigation: NavigationService,
    accountService: UserAccountService
  ) {
    super(activatedRoute, service, messageService, navigation, commandDispatcher, handlerResolver);

    this.addDeleteMenu = false;
    this.AddSubscription(
      accountService.current.subscribe(account => {
        if (this.canManageIncorrectPayments || !account.hasAccess(ModuleNames.Invoicing, Features.ManageIncorrectPayments)) {
          return;
        }

        this.canManageIncorrectPayments = true;
        this.addMenuItemDefinition('INCORRECT_PAYMENT.COMMAND.PAY_INVOICE', IncorrectPaymentCommand.payInvoice);
        this.addMenuItemDefinition('INCORRECT_PAYMENT.COMMAND.CUSTOMER_BALANCE', IncorrectPaymentCommand.customerBalance);
        this.addMenuItemDefinition('INCORRECT_PAYMENT.COMMAND.CHANGE_PAYER', IncorrectPaymentCommand.changePayer);
        this.addMenuItemDefinition('INCORRECT_PAYMENT.COMMAND.VOID', IncorrectPaymentCommand.voidPayment);
      })
    );

    this.columnTransactionsDefinition = [
      factory.createDate('ACCOUNT.ACCOUNTING_DATE', 'businessTransactionDate'),
      factory.create('INCORRECT_PAYMENT.DESCRIPTION', 'description'),
      factory.createMoney('INCORRECT_PAYMENT.AMOUNT', 'amount'),
      factory.create('VERIFICATION.NUMBER', 'verificationNumber'),
      factory.createDate('COMMON.CREATED_DATE', 'creationDate', ColumnVisibility.optional),
    ];

    this.columnAccountingsDefinition = [
      factory.create('VERIFICATION.NUMBER', 'verificationNumber'),
      factory.create('VERIFICATION.DESCRIPTION', 'verificationDescription'),
      factory.create('VERIFICATION.CREATED_DATE', 'creationDate'),
      factory.create('ACCOUNT.ACCOUNTING_DATE', 'businessTransactionDate'),
      factory.create('ACCOUNT.NUMBER', 'accountNumber'),
      factory.create('ACCOUNT.DEBIT', 'debit'),
      factory.create('ACCOUNT.CREDIT', 'credit'),
      factory.create(defaultDimensionLabels.costPlaceLabel, 'costPlace'),
      factory.create(defaultDimensionLabels.costBearerLabel, 'costBearer', ColumnVisibility.optional),
      factory.create(defaultDimensionLabels.counterpartLabel, 'counterpart', ColumnVisibility.optional),
      factory.create(defaultDimensionLabels.projectLabel, 'project'),
    ];
  }

  columnTransactionsDefinition: ColumnDefinition[];

  columnAccountingsDefinition: ColumnDefinition[];

  protected createModel(): IncorrectPayment {
    return new IncorrectPayment();
  }

  hasOcr: boolean;
  hasPayerName: boolean;
  hasBankgiroNumber: boolean;

  protected getFormGroup(): FormGroup {
    this.hasOcr = this.dataContext.ocr !== undefined && this.dataContext.ocr !== '';
    this.hasPayerName = this.dataContext.payerNameAndAddress !== undefined && this.dataContext.payerNameAndAddress !== '';
    this.hasBankgiroNumber = this.dataContext.bankgiroNumber !== undefined && this.dataContext.bankgiroNumber !== '';
    return this._fb.group({
      description: this._fc.createReadonly('INCORRECT_PAYMENT.DESCRIPTION', this.dataContext.description),
      payerNameAndAddress: this._fc.createReadonly('INCORRECT_PAYMENT.PAYER', this.dataContext.payerNameAndAddress),
      amount: this._fc.createReadonly('INCORRECT_PAYMENT.AMOUNT', this.dataContext.amount),
      balance: this._fc.createReadonly('INCORRECT_PAYMENT.BALANCE', this.dataContext.balance),
      companyName: this._fc.createReadonly('COMPANY.EDIT_HEADER', this.dataContext.companyName),
      bankgiroNumber: this._fc.createReadonly('COMPANY.BANK_GIRO', this.dataContext.bankgiroNumber),
      ocr: this._fc.createReadonly('INVOICE.OCRNUMBER', this.dataContext.ocr),
      registrationTypeText: this._fc.createReadonly('PAYMENT.REGISTRATION_TYPE', this.dataContext.registrationTypeText),
      businessTransactionDate: this._fb.group({}),
    });
  }

  showTransactions: boolean;

  protected init(data: IncorrectPayment) {
    super.init(data);
    this.showTransactions = data.incorrectPaymentTransactions && data.incorrectPaymentTransactions.length > 0;
    this.AddSubscription(
      this._companyService
        .getList()
        .pipe(
          map(companies => companies.find(company => company.name === data.companyName || company.toString() === data.companyName)),
          switchMap(company => this._dimensionLabelsService.getForCompany(company?.id))
        )
        .subscribe(labels => this.applyDimensionLabels(labels))
    );
  }

  protected prepareSave() {
    const model = super.prepareSave();
    model.businessTransactionDate = this.dataContext.businessTransactionDate;
    model.companyAccountingClosedToDate = this.dataContext.companyAccountingClosedToDate;
    model.creationDate = this.dataContext.creationDate;
    model.id = this.dataContext.id;
    return model;
  }

  private applyDimensionLabels(labels: DimensionLabels) {
    this._dimensionLabelsService.setColumnCaption(this.columnAccountingsDefinition, 'costPlace', labels.costPlaceLabel);
    this._dimensionLabelsService.setColumnCaption(this.columnAccountingsDefinition, 'costBearer', labels.costBearerLabel);
    this._dimensionLabelsService.setColumnCaption(this.columnAccountingsDefinition, 'counterpart', labels.counterpartLabel);
    this._dimensionLabelsService.setColumnCaption(this.columnAccountingsDefinition, 'project', labels.projectLabel);
    this.columnAccountingsDefinition = [...this.columnAccountingsDefinition];
  }
}
